// useDynamicColumns.tsx

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Check, X } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../ui';

// Types
export interface Column {
  title: string;
}

// Core columns that cannot be removed
const CORE_COLUMNS = [
  'Applicant',
  'CV Score',
  'Status',
  'Location',
  'Experience',
  'Age',
  'Gender',
  'Skills',
];

const getStatusBadgeSx = (status: string) => {
  switch (status) {
    case 'New':
      return { bgcolor: '#ecfeff', border: '1px solid #a5f3fc', color: '#0e7490' };
    case 'Suitable':
      return { bgcolor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' };
    case 'Outstanding':
      return { bgcolor: '#faf5ff', border: '1px solid #e9d5ff', color: '#7e22ce' };
    case 'Shortlisted':
      return { bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' };
    case 'Auto-Assessment':
      return { bgcolor: '#fffbeb', border: '1px solid #fde68a', color: '#b45309' };
    case 'Unsuccessful-Pending':
      return { bgcolor: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c' };
    case 'Unsuccessful-Closed':
      return { bgcolor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' };
    default:
      return { bgcolor: '#f9fafb', border: '1px solid #e5e7eb', color: '#4b5563' };
  }
};

const getStatusDotColor = (status: string) => {
  switch (status) {
    case 'New':
      return '#06b6d4';
    case 'Suitable':
      return '#3b82f6';
    case 'Outstanding':
      return '#a855f7';
    case 'Shortlisted':
      return '#10b981';
    case 'Auto-Assessment':
      return '#f59e0b';
    case 'Unsuccessful-Pending':
      return '#f97316';
    case 'Unsuccessful-Closed':
      return '#ef4444';
    default:
      return '#9ca3af';
  }
};

const getScoreColor = (num: number): { bar: string; text: string } => {
  if (num > 90) return { bar: '#22c55e', text: '#15803d' };
  if (num > 70) return { bar: '#3b82f6', text: '#1d4ed8' };
  if (num > 50) return { bar: '#f97316', text: '#c2410c' };
  return { bar: '#ef4444', text: '#b91c1c' };
};

// Custom Hook
export const GetColumns = () => {
  const [columns, setColumns] = useState<Column[]>([
    { title: 'Applicant' },
    { title: 'CV Score' },
    { title: 'Status' },
    { title: 'Location' },
    { title: 'Experience' },
    { title: 'Age' },
    { title: 'Gender' },
    { title: 'Skills' },
  ]);

  const getColumns = columns.map((col) => ({
    key: col.title,
    label: col.title,
    width:
      col.title === 'Applicant'
        ? '230px'
        : col.title === 'Status'
          ? '160px'
          : col.title === 'Location'
            ? '160px'
            : '110px',
    align: 'left' as const,
    render: (row: any) => {
      // --- Applicant: avatar + name + email ---
      if (col.title === 'Applicant') {
        const name = (row['Applicant'] as string) || '';
        const email = (row['Email'] as string) || '';
        const avatar = (row['Avatar'] as string) || '';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25 }}>
            <NameEmailAvatar avatar={avatar} name={name} email={email} />
          </Box>
        );
      }

      // --- CV Score: colored score chip ---
      if (col.title === 'CV Score') {
        const num = parseFloat(row['CV Score'] as string);
        const val = isNaN(num) ? 0 : num;
        const { bar, text } = getScoreColor(val);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 80, height: 6, borderRadius: '9999px', bgcolor: '#e5e7eb', overflow: 'hidden' }}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '9999px',
                  bgcolor: bar,
                  width: `${Math.min(val, 100)}%`,
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: text }}>
              {row['CV Score']}
            </Typography>
          </Box>
        );
      }

      // --- Status: colored pill badge ---
      if (col.title === 'Status') {
        const status = (row['Status'] as string) || '';
        const label = status.replace('Unsuccessful-', 'Unsuccessful\u00A0');
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                ...getStatusBadgeSx(status),
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: getStatusDotColor(status),
                }}
              />
              {label || '—'}
            </Box>
          </Box>
        );
      }

      // --- Location: city + country stacked ---
      if (col.title === 'Location') {
        const city = (row['City'] as string) || '';
        const country = (row['Country'] as string) || '';
        if (!city && !country)
          return <Typography variant="body2" sx={{ color: '#9ca3af' }}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
            <Box sx={{ fontSize: '0.875rem', lineHeight: 1.25 }}>
              {city && (
                <Typography variant="body2" noWrap sx={{ color: '#1f2937', fontWeight: 500 }}>
                  {city}
                </Typography>
              )}
              {country && (
                <Typography variant="caption" noWrap sx={{ color: '#9ca3af', display: 'block' }}>
                  {country}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }

      // --- Skill columns (dynamic): Yes/No icons ---
      if (!CORE_COLUMNS.includes(col.title)) {
        const val = row[col.title];
        return val === 'Yes' ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                color: '#16a34a',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              <Check size={12} strokeWidth={3} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: '#fee2e2',
                color: '#f87171',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              <X size={12} strokeWidth={3} />
            </Box>
          </Box>
        );
      }

      // --- Default plain text ---
      const value = row[col.title];
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {value || '—'}
          </Typography>
        </Box>
      );
    },

    disableSort: !['CV Score', 'Experience', 'Age', 'Skills'].includes(
      col.title,
    ),
  }));

  return { columns, setColumns, getColumns };
};

export default GetColumns;
